type Events = Record<string, any>;

type Callback<Data> = (event: Data, unsubscribe: () => number) => void;

type EventType = string | number | symbol;

type Options<TEvents extends Events> = {
  /**
   * Callback for error handling.
   *
   * @param error The error that occurred
   * @returns Whether to rethrow the error
   */
  onEmitError?: (
    opts: {
      [K in keyof TEvents]: { type: K; data: TEvents[K]; error: Error };
    }[keyof TEvents]
  ) => boolean;
};

/**
 * Create a typed event emitter.
 *
 * To type events, provide a type that maps event type name > event data.
 *
 * Set the data's type to `void` or `undefined` to define an empty event.
 *
 * Set the key to a string to allow arbitrary string types that map to a specific
 * event shape.
 *
 * @param opts - The options for the event emitter
 * @param opts.onEmitError - Optional callback for error handling
 * @returns The event emitter with methods:
 * - `.count(type)` to count event subscribers (does not count wildcard subscribers)
 * - `.on(type, callback: (data, off))` to subscribe to an event
 * - `.all({ type, data }, off)` to subscribe to all events (a "wildcard subscriber")
 * - `.emit(type, data)` to emit an event
 * - `.teardown()` to cleanup the emitter
 *
 * @example
 * ```typescript
 * // Simple event type
 * const emitter = create<{ 'test-type': string }>();
 *
 * emitter.on('test-type', (data) => {
 *   console.log(data);
 * });
 *
 * emitter.emit('test-type', 'test');
 *
 * // Arbitrary string type with known shape
 * const emitter = create<{ [key: string]: number }>();
 *
 * emitter.on('someKey', callback);
 *
 * emitter.emit('someKey', 123);
 *
 * // A mix of types
 * const emitter = create<{ 'test-type': string; [key: string]: number }>();
 *
 * emitter.on('test-type', (data) => {
 *   console.log(data);
 * });
 *
 * emitter.emit('test-type', 'test');
 * emitter.emit('arbitrary', 123);
 *
 * ```
 */
export const create = <
  TEvents extends Record<string, any> = Record<string, unknown>,
>(
  opts: Options<TEvents> = {}
) => {
  const handlers = new Map<EventType, Set<Callback<any>>>();

  const remove = (type: EventType, callback: Callback<any>) => {
    const callbacks = handlers.get(type);

    if (!callbacks) {
      return 0;
    }

    callbacks.delete(callback);

    if (callbacks.size === 0) {
      handlers.delete(type);
      return 0;
    }

    return callbacks.size;
  };

  function emit<K extends keyof TEvents>(type: K, data: TEvents[K]): void;
  function emit<K extends keyof TEvents>(
    type: TEvents[K] extends void | undefined ? K : never
  ): void;
  function emit<K extends keyof TEvents>(type: K, data?: TEvents[K]) {
    const callbacks = handlers.get(type);

    if (!callbacks) {
      return;
    }

    for (const callback of callbacks) {
      try {
        callback(data, () => remove(type, callback));
      } catch (error) {
        if (
          opts.onEmitError?.({
            type: type as keyof TEvents,
            data: data as TEvents[keyof TEvents],
            error: error as Error,
          })
        ) {
          throw error;
        }
      }
    }
  }

  return {
    /**
     * Get the current subscriber count for a specific event.
     *
     * @param type The event type to count
     * @returns The number of subscribers for this event
     *
     * @example
     * ```typescript
     * const off = emitter.on('event', callback);
     * emitter.count('event'); // 1
     * off();
     * emitter.count('event'); // 0
     *
     * ```
     */
    count: <K extends keyof TEvents>(type: K) => handlers.get(type)?.size ?? 0,
    /**
     * Get the current subscriber count for all events.
     *
     * @returns An object with event type as key and subscriber count as value
     *
     * @example
     * ```typescript
     * const emitter = create<{ event: string; anotherEvent: number }>();
     * const counts = emitter.countEach();
     * console.log(counts); // { event: 1, anotherEvent: 2 }
     * ```
     */
    countAll: () => {
      const counts: Record<string, number> = {};

      for (const [type, callbacks] of handlers) {
        counts[type as string] = callbacks.size;
      }

      return counts;
    },
    /**
     * Subscribe to an event.
     *
     * Returns an `off` callback to unsubscribe. `off`:
     * - returns the updated subscriber count
     * - is also provided as a second arg to the callback
     *
     * @param type The event type to subscribe to
     * @param callback The callback to call when the event is emitted
     * @returns A function to unsubscribe from the event
     *
     * @example
     * ```typescript
     * const off = emitter.on('event', console.log);
     *
     * emitter.emit('event', 'test'); // 'test'
     * off();
     * emitter.emit('event', 'test'); // nothing
     *
     * ```
     */
    on: <K extends keyof TEvents>(type: K, callback: Callback<TEvents[K]>) => {
      let callbacks = handlers.get(type);

      if (!callbacks) {
        callbacks = new Set();
        handlers.set(type, callbacks);
      }

      callbacks.add(callback);

      return () => remove(type, callback);
    },
    /**
     * Emit an event of the specified type.
     *
     * If the data is typed as void or undefined, will not accept a data param.
     *
     * Prevents recursive emissions by throwing immediately (cannot be stopped by the error callback)
     *
     * @param type The event type to emit
     * @param data The data to emit
     *
     * @example
     * ```typescript
     * emitter.emit('event', 'test');
     * ```
     */
    emit,
    /**
     * Cleanup the emitter.
     */
    teardown: () => {
      handlers.clear();
    },
  };
};

export type EventEmitter<
  TEvents extends Record<string, any> = Record<string, unknown>,
> = ReturnType<typeof create<TEvents>>;
